import { camelize } from 'helpers/syntaxHelper';
import { useState } from 'react';
import { UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import { useGetBudgetUserExerciceSettings } from 'routes/BudgetModule/components/shared/BudgetReportToggle/hooks';
import { BUDGET_PROPOSAL_CUSTOM_FILTER_VALUES } from 'routes/BudgetModule/config/constants';
import {
    BudgetProposalT,
    BudgetVentilatedDataT,
} from 'routes/BudgetModule/config/types';
import { createBudgetQueryKeys } from 'routes/BudgetModule/helpers/createBudgetQueryKeys';
import {
    useBudgetProposalsQuery,
    useVentilatedBudgetProposalQuery,
} from 'routes/BudgetModule/helpers/hooks/useCustomQuery';
import { getBudgetLineCodeFilter } from 'routes/BudgetModule/helpers/query';
import { IntegrationT } from 'routes/IntegrationsList/types';
import { HttpError, RQAPICallWithData } from 'store/APIClient';
import {
    AutocompletAble,
    formatValueForAutocomplete,
} from 'ui/DropdownItems/types';

export const useGetBudgetProposal = ({
    budgetProposalId,
    queryString,
}: {
    budgetProposalId: number;
    queryString?: string;
}): UseQueryResult<BudgetProposalT, HttpError> => {
    const { budgetProposalsKeys } = createBudgetQueryKeys();
    return useBudgetProposalsQuery(
        budgetProposalsKeys({ budgetProposalId }),
        async () => {
            const response = await RQAPICallWithData<BudgetProposalT>({
                endpoint: `budget_proposals/${budgetProposalId}${
                    queryString || ''
                }`,
                method: 'GET',
                postProcessingCallback: (payload) => ({
                    ...payload,
                    data: {
                        ...payload.data,
                        by: camelize(payload.data.by),
                        projectionBy: payload.data.projectionBy.map(camelize),
                        pluriannualProjectionBy:
                            payload.data.pluriannualProjectionBy.map(camelize),
                    },
                }),
            });

            return response.content.data;
        },
    );
};

export const isCustomBudgetProposal = (
    integrations?: IntegrationT[],
): boolean =>
    integrations?.some((integration) =>
        Object.keys(BUDGET_PROPOSAL_CUSTOM_FILTER_VALUES).includes(
            integration.software,
        ),
    ) || false;

export const getCustomFilterLabel = (integrations?: IntegrationT[]): string => {
    if (integrations === undefined || !integrations[0]) {
        return '';
    }
    return BUDGET_PROPOSAL_CUSTOM_FILTER_VALUES[integrations[0].software].label;
};

const translateValueToLabel = ({ value }: { value: string }): string => {
    if (value === '9F') return 'CP/RE';
    if (value === '9G') return 'AE/AR';
    return value;
};

export const useGetCustomFilters = (integrations?: IntegrationT[]) => {
    let initFilter = '';
    let codeOptions: AutocompletAble<string>[] = [];
    if (integrations !== undefined && integrations[0]) {
        const targetFilters =
            BUDGET_PROPOSAL_CUSTOM_FILTER_VALUES[integrations[0].software] ||
            BUDGET_PROPOSAL_CUSTOM_FILTER_VALUES.default;
        codeOptions = targetFilters.values.map((el) =>
            formatValueForAutocomplete(
                el,
                false,
                translateValueToLabel({ value: el }),
            ),
        );
        initFilter = targetFilters.values[0];
    }
    const [code, setCodes] = useState<AutocompletAble<string> | null>(
        initFilter
            ? formatValueForAutocomplete(
                  initFilter,
                  false,
                  translateValueToLabel({ value: initFilter }),
              )
            : null,
    );
    const codeFilters = isCustomBudgetProposal(integrations)
        ? [getBudgetLineCodeFilter(code!.value)]
        : [];

    return {
        code,
        setCodes,
        codeFilters,
        codeOptions,
    };
};

const createBudgetVentilatedDataEndpoint = (
    budgetProposalId: number,
    isOperation: boolean,
    budgetLineCode: string | undefined,
): string => {
    const budgetLineCodeArg = budgetLineCode
        ? `?budget_line_code=${budgetLineCode}`
        : '';
    const endpoint = isOperation
        ? `budget_proposals/${budgetProposalId}/get_budget_ventilated_data?is_operation=true`
        : `budget_proposals/${budgetProposalId}/get_budget_ventilated_data${budgetLineCodeArg}`;
    return endpoint;
};

type UseGetBudgetVentilatedDataProps = {
    budgetProposalId: number;
    exercice: number;
    isOperation?: boolean;
    budgetLineCode?: string;
    params?: Pick<
        UseQueryOptions<BudgetVentilatedDataT[], HttpError>,
        'enabled'
    >;
};

/**
 * Custom hook to fetch ventilated budget data.
 *
 * @param budgetProposalId - The ID of the budget proposal to fetch data for.
 * @param exercice - The exercise for which data is being fetched.
 * @param isOperation - A flag indicating whether the data is for an operation.
 * @param budgetLineCode - Optional budget line code to filter the data (SIFAC).
 * @param params - Additional parameters to be passed to the query.
 *
 * @returns - The result of the query containing the ventilated budget data or an error.
 */
export const useGetBudgetVentilatedData = ({
    budgetProposalId,
    exercice,
    isOperation = false,
    budgetLineCode = undefined,
    params = {},
}: UseGetBudgetVentilatedDataProps): UseQueryResult<
    BudgetVentilatedDataT[],
    HttpError
> => {
    const { ventilatedBudgetProposalKeys } = createBudgetQueryKeys();
    const { budgetReportExerciceData } =
        useGetBudgetUserExerciceSettings(exercice);
    return useVentilatedBudgetProposalQuery(
        ventilatedBudgetProposalKeys({
            budgetProposalId,
            isOperation,
            isReportedAmountUsed:
                budgetReportExerciceData?.isReportedAmountUsed,
            budgetLineCode,
        }),
        async () => {
            const response = await RQAPICallWithData<BudgetVentilatedDataT[]>({
                endpoint: createBudgetVentilatedDataEndpoint(
                    budgetProposalId,
                    isOperation,
                    budgetLineCode,
                ),
                method: 'GET',
            });

            return response.content.data;
        },
        params,
    );
};
